'use client'

import { motion } from 'framer-motion'
import { Mail, Loader2, CheckCircle2 } from 'lucide-react'
import { getPrimeiroNome } from '../utilitarios'
import type { RefObject } from 'react'

interface StepContatoProps {
  nomeCompleto: string
  email: string
  setEmail: (valor: string) => void
  telefone: string
  setTelefone: (valor: string) => void
  erros: { email: string; phone: string }
  setErros: (fn: (prev: { email: string; phone: string }) => { email: string; phone: string }) => void
  onSubmit: () => void
  onBack: () => void
  inputRef: RefObject<HTMLInputElement | null>
  validandoWhatsApp?: boolean
  whatsappVerificado?: boolean | null
}

export function StepContato({ nomeCompleto, email, setEmail, telefone, setTelefone, erros, setErros, onSubmit, onBack, inputRef, validandoWhatsApp, whatsappVerificado }: StepContatoProps) {
  const borderClass = erros.phone
    ? 'border-red-500/50'
    : whatsappVerificado === true
      ? 'border-green-500/50'
      : 'border-purple-500/20 dark:border-white/10 focus:border-purple-500/50 focus:bg-white/90 dark:focus:bg-white/8'

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-md mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-4 sm:mb-6">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg shadow-purple-500/30">
          <Mail size={24} strokeWidth={1.5} className="text-white" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-indigo-950 dark:text-white mb-2">Prazer, {getPrimeiroNome(nomeCompleto)}!</h2>
        <p className="text-indigo-950/50 dark:text-white/50 text-sm">Como podemos entrar em contato?</p>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
        <div>
          <label className="text-sm text-indigo-950/60 dark:text-white/60 mb-1 block">Email</label>
          <input ref={inputRef} type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (erros.email) setErros(prev => ({ ...prev, email: '' })) }} placeholder="seu@email.com" className={`w-full px-4 py-3 rounded-xl bg-white/85 dark:bg-white/5 border text-indigo-950 dark:text-white placeholder:text-indigo-950/30 dark:placeholder:text-white/30 outline-none transition-all ${erros.email ? 'border-red-500/50' : 'border-purple-500/20 dark:border-white/10 focus:border-purple-500/50 focus:bg-white/90 dark:focus:bg-white/8'}`} autoComplete="email" />
          {erros.email && <p className="mt-1 text-xs text-red-400">{erros.email}</p>}
        </div>
        <div>
          <label className="text-sm text-indigo-950/60 dark:text-white/60 mb-1 block">WhatsApp</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-950/40 dark:text-white/40 text-sm">+55</span>
            <input
              type="tel"
              inputMode="tel"
              value={telefone}
              onChange={(e) => { setTelefone(e.target.value); if (erros.phone) setErros(prev => ({ ...prev, phone: '' })); }}
              placeholder="(00) 00000-0000"
              className={`w-full pl-12 pr-10 py-3 rounded-xl bg-white/85 dark:bg-white/5 border text-indigo-950 dark:text-white placeholder:text-indigo-950/30 dark:placeholder:text-white/30 outline-none transition-all ${borderClass}`}
              autoComplete="tel"
              disabled={validandoWhatsApp}
            />
            {validandoWhatsApp && (
              <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 animate-spin" />
            )}
            {!validandoWhatsApp && whatsappVerificado === true && (
              <CheckCircle2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
            )}
          </div>
          {erros.phone && <p className="mt-1 text-xs text-red-400">{erros.phone}</p>}
        </div>
        <button
          onClick={onSubmit}
          disabled={validandoWhatsApp}
          className={`w-full py-3.5 rounded-full font-medium text-sm shadow-lg shadow-purple-600/25 transition-all active:scale-[0.98] ${
            validandoWhatsApp
              ? 'bg-purple-400 cursor-not-allowed text-white/70'
              : 'bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white'
          }`}
        >
          {validandoWhatsApp ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Verificando WhatsApp...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              Continuar
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
          )}
        </button>
      </motion.div>
      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} onClick={onBack} className="mt-4 w-full text-center text-sm text-indigo-950/40 dark:text-white/40 hover:text-indigo-950/60 dark:text-white/60 transition-colors">&larr; Voltar</motion.button>
    </motion.div>
  )
}
